/**
 * SASADM-0149 (saas-auth SASATH-0331) — product Agents tab.
 *
 * SUPER_ADMIN-only section mounted on the product detail screen (the caller in
 * ProductDetail gates rendering on `operator?.isSuperAdmin`; this component
 * never fetches for a non-super principal and degrades to a contained
 * "Super admin required" state on a live 403). Self-fetches agent clients +
 * the published scope catalog on mount with isolated loading/empty/error
 * states — it is NOT folded into the shared productDetailLoader.
 *
 * Distinct from Connected apps (OIDC): public OAuth 2.1 + PKCE agent clients
 * (no secret, no delete — disable only). `authorizeUrl` / `tokenUrl` render
 * exactly as the API returned them; they are never composed from host/slug.
 * The scope multi-select offers only catalog scopes (subset-of-catalog).
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState, type MouseEvent } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError, type ErrorCode } from '@/api/errors';
import type { AgentClientResponse, ProductScopeDto, TenantDto } from '@/api/types';
import { useCurrentOperator } from '@/auth/AuthContext';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

/** Server-side max `size` (SASATH-0212 clamps anything larger). */
const MAX_PAGE_SIZE = 200;
const CLIENT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;
const MAX_REDIRECT_URIS = 10;

const CREATE_FIELD_CODES: readonly ErrorCode[] = [
  'INVALID_CLIENT_ID',
  'CLIENT_ID_TAKEN',
  'INVALID_REDIRECT_URI',
  'INVALID_SCOPE',
  'TENANT_NOT_IN_PRODUCT',
  'TENANT_NOT_ACTIVE',
];

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

function asList<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

/** Exact-match redirect URIs the agent API accepts (no fragment). */
function isAllowedRedirectUri(raw: string): boolean {
  const uri = raw.trim();
  if (!uri || uri.includes('#')) return false;
  try {
    const parsed = new URL(uri);
    if (parsed.hash) return false;
    if (parsed.protocol === 'https:') return true;
    if (parsed.protocol === 'http:') {
      return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    }
    return Boolean(parsed.protocol) && parsed.protocol !== 'http:' && parsed.protocol !== 'https:';
  } catch {
    return false;
  }
}

export function ProductAgentClients({
  productId,
  productSlug,
}: {
  productId: number;
  productSlug: string;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const isSuperAdmin = operator?.isSuperAdmin === true;

  const [clients, setClients] = useState<AgentClientResponse[] | null>(null);
  const [catalog, setCatalog] = useState<ProductScopeDto[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<AgentClientResponse | null>(null);
  const [confirmDeleteScope, setConfirmDeleteScope] = useState<ProductScopeDto | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoadError(null);
    try {
      const [clientList, scopeList] = await Promise.all([
        apiAdmin.agentClients.list(productId),
        apiAdmin.agentClients.listScopes(productId),
      ]);
      setClients(asList(clientList));
      setCatalog(asList(scopeList));
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setClients([]);
        setCatalog([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setClients([]);
      setCatalog([]);
    }
  }

  useEffect(() => {
    if (!isSuperAdmin) return;
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isSuperAdmin]);

  function applyClient(updated: AgentClientResponse) {
    setClients((prev) =>
      prev ? prev.map((row) => (row.clientDbId === updated.clientDbId ? updated : row)) : prev,
    );
    setSelected((current) => (current?.clientDbId === updated.clientDbId ? updated : current));
  }

  async function handleSetEnabled(client: AgentClientResponse, enabled: boolean) {
    setBusy(true);
    try {
      const updated = enabled
        ? await apiAdmin.agentClients.enable(productId, client.clientDbId)
        : await apiAdmin.agentClients.disable(productId, client.clientDbId);
      applyClient(updated);
      snack.success(
        enabled
          ? t('products.agents.enabledSnack', 'Agent client enabled.')
          : t('products.agents.disabledSnack', 'Agent client disabled.'),
      );
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteScope() {
    if (!confirmDeleteScope) return;
    setBusy(true);
    try {
      await apiAdmin.agentClients.deleteScope(productId, confirmDeleteScope.scope);
      snack.success(t('products.agents.scopes.deleted', 'Scope deleted.'));
      setConfirmDeleteScope(null);
      await refresh();
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmDeleteScope(null);
        return;
      }
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setConfirmDeleteScope(null);
    } finally {
      setBusy(false);
    }
  }

  if (!isSuperAdmin || forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('products.agents.title', 'Agent clients')}
        </Typography>
        <Alert severity="warning">
          {t('products.agents.superAdminRequired', 'Super admin required to manage agent clients.')}
        </Alert>
      </Paper>
    );
  }

  const catalogScopes = (catalog ?? []).map((row) => row.scope);
  const loading = clients === null || catalog === null;

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h3">{t('products.agents.title', 'Agent clients')}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            {t('products.agents.new', 'New agent client')}
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {t(
            'products.agents.explanation',
            'Public OAuth 2.1 + PKCE clients bound to this product and one login tenant. Disable stops new authorizations; there is no delete.',
          )}
        </Typography>
        <ErrorBanner error={loadError} />
        {loading ? (
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        ) : clients.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>
            {t('products.agents.empty', 'No agent clients yet.')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.agents.columns.clientName', 'Name')}</TableCell>
                  <TableCell>{t('products.agents.columns.clientId', 'Client ID')}</TableCell>
                  <TableCell>{t('products.agents.columns.loginTenant', 'Login tenant')}</TableCell>
                  <TableCell>{t('products.agents.columns.scopes', 'Scopes')}</TableCell>
                  <TableCell>{t('products.agents.columns.enabled', 'Enabled')}</TableCell>
                  <TableCell>{t('products.agents.columns.createdAt', 'Created')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow
                    key={client.clientDbId}
                    hover
                    onClick={() => setSelected(client)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{client.clientName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {client.clientId}
                      </Typography>
                    </TableCell>
                    <TableCell>{client.loginTenantSlug}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {client.scopes.map((scope) => (
                          <Chip key={scope} label={scope} size="small" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={client.enabled}
                        disabled={busy}
                        onClick={(event: MouseEvent) => event.stopPropagation()}
                        onChange={(_event, checked) => {
                          void handleSetEnabled(client, checked);
                        }}
                        slotProps={{
                          input: {
                            'aria-label': t('products.agents.columns.enabled', 'Enabled'),
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(client.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('products.agents.scopes.title', 'Scopes')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {t(
            'products.agents.scopes.explanation',
            'Published by the product backend. Agents request these at sign-in; the person sees the description on the consent page.',
          )}
        </Typography>
        {loading ? (
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        ) : catalog.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>
            {t('products.agents.scopes.empty', 'This product has not published any scopes yet.')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.agents.scopes.columns.scope', 'Scope')}</TableCell>
                  <TableCell>
                    {t('products.agents.scopes.columns.description', 'Description')}
                  </TableCell>
                  <TableCell>{t('products.agents.scopes.columns.updatedAt', 'Updated')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catalog.map((row) => (
                  <TableRow key={row.scope}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {row.scope}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(row.updatedAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={t('common.delete', 'Delete')}
                        title={t('common.delete', 'Delete')}
                        onClick={() => setConfirmDeleteScope(row)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <CreateAgentClientDialog
        open={createOpen}
        productId={productId}
        productSlug={productSlug}
        catalogScopes={catalogScopes}
        onClose={() => setCreateOpen(false)}
        onForbidden={() => {
          setCreateOpen(false);
          setForbidden(true);
        }}
        onCreated={async (created) => {
          setCreateOpen(false);
          setSelected(created);
          await refresh();
        }}
      />

      <AgentClientDrawer
        client={selected}
        productId={productId}
        catalogScopes={catalogScopes}
        busy={busy}
        onClose={() => setSelected(null)}
        onForbidden={() => setForbidden(true)}
        onUpdated={applyClient}
        onToggleEnabled={(enabled) => {
          if (selected) void handleSetEnabled(selected, enabled);
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteScope)}
        title={t('products.agents.scopes.deleteTitle', 'Delete scope?')}
        body={t(
          'products.agents.scopes.deleteBody',
          'Agents that were granted this scope lose it on their next token refresh.',
        )}
        severity="error"
        confirmLabel={t('common.delete', 'Delete')}
        busy={busy}
        onConfirm={() => void handleDeleteScope()}
        onCancel={() => setConfirmDeleteScope(null)}
      />
    </Stack>
  );
}

interface CreateAgentClientDialogProps {
  open: boolean;
  productId: number;
  productSlug: string;
  catalogScopes: string[];
  onClose: () => void;
  onForbidden: () => void;
  onCreated: (created: AgentClientResponse) => void | Promise<void>;
}

function CreateAgentClientDialog({
  open,
  productId,
  productSlug,
  catalogScopes,
  onClose,
  onForbidden,
  onCreated,
}: CreateAgentClientDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [loginTenantId, setLoginTenantId] = useState<number | ''>('');
  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  /** null = not yet settled (loading); [] = loaded empty. */
  const [tenants, setTenants] = useState<TenantDto[] | null>(null);
  const [tenantsLoadFailed, setTenantsLoadFailed] = useState(false);
  const [tenantsRetryKey, setTenantsRetryKey] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);
  const [uriHint, setUriHint] = useState<string | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [pending, setPending] = useState(false);

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setClientId('');
      setClientName('');
      setLoginTenantId('');
      setRedirectUris([]);
      setScopes([]);
      setTenants(null);
      setTenantsLoadFailed(false);
      setError(null);
      setUriHint(null);
    }
  }

  useEffect(() => {
    if (!open) return;
    let current = true;
    void (async () => {
      try {
        const page = await apiAdmin.tenants.listPaged({
          productId,
          status: 'ACTIVE',
          size: MAX_PAGE_SIZE,
        });
        if (current) {
          setTenants(asList(page.content));
          setTenantsLoadFailed(false);
        }
      } catch (err) {
        if (isForbidden(err)) {
          onForbidden();
          return;
        }
        if (current) {
          setTenants(null);
          setTenantsLoadFailed(true);
        }
      }
    })();
    return () => {
      current = false;
    };
    // onForbidden is an identity-unstable parent callback; the 403 path is the
    // only consumer and is not a fetch input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId, tenantsRetryKey]);

  const fieldErrorCode =
    error && CREATE_FIELD_CODES.includes(error.code) ? error.code : null;
  const fieldErrorMessage = fieldErrorCode
    ? t(`products.agents.errors.${fieldErrorCode}`, fieldErrorCode)
    : null;
  const clientIdErrored =
    fieldErrorCode === 'INVALID_CLIENT_ID' || fieldErrorCode === 'CLIENT_ID_TAKEN';
  const tenantErrored =
    fieldErrorCode === 'TENANT_NOT_IN_PRODUCT' || fieldErrorCode === 'TENANT_NOT_ACTIVE';
  const redirectErrored = fieldErrorCode === 'INVALID_REDIRECT_URI';
  const scopeErrored = fieldErrorCode === 'INVALID_SCOPE';

  const canSubmit =
    CLIENT_ID_PATTERN.test(clientId.trim()) &&
    clientName.trim().length > 0 &&
    loginTenantId !== '' &&
    redirectUris.length >= 1 &&
    redirectUris.length <= MAX_REDIRECT_URIS;

  async function onSubmit() {
    if (loginTenantId === '') return;
    setError(null);
    setPending(true);
    try {
      const created = await apiAdmin.agentClients.create(productId, {
        clientId: clientId.trim(),
        clientName: clientName.trim(),
        loginTenantId,
        redirectUris,
        scopes,
      });
      snack.success(t('products.agents.created', 'Agent client created.'));
      await onCreated(created);
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('products.agents.createTitle', 'New agent client')}</DialogTitle>
      <DialogContent>
        {fieldErrorCode ? null : <ErrorBanner error={error} />}
        <Box
          component="form"
          id="agent-client-form"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit();
          }}
          noValidate
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('products.agents.fields.clientId', 'Client ID')}
              value={clientId}
              onChange={(event) => {
                setClientId(event.target.value);
                setError(null);
              }}
              error={clientIdErrored}
              helperText={
                fieldErrorCode === 'INVALID_CLIENT_ID' || fieldErrorCode === 'CLIENT_ID_TAKEN'
                  ? fieldErrorMessage
                  : t(
                      'products.agents.fields.clientIdHelper',
                      'Must match {{pattern}}.',
                      { pattern: '^[a-z0-9][a-z0-9-]{2,63}$' },
                    )
              }
              slotProps={{ htmlInput: { sx: { fontFamily: 'monospace' } } }}
            />
            <TextField
              fullWidth
              required
              label={t('products.agents.fields.clientName', 'Name')}
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
            />
            {tenantsLoadFailed ? (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setTenants(null);
                      setTenantsLoadFailed(false);
                      setTenantsRetryKey((key) => key + 1);
                    }}
                  >
                    {t('common.retry', 'Retry')}
                  </Button>
                }
              >
                {t(
                  'products.agents.tenantsLoadError',
                  "Active tenants couldn't be loaded.",
                )}
              </Alert>
            ) : tenants === null ? (
              <Typography color="text.secondary">
                {t('common.loading', 'Loading')}
              </Typography>
            ) : tenants.length === 0 ? (
              <Typography color="text.secondary">
                {t(
                  'products.agents.tenantsEmpty',
                  'No active tenants for this product.',
                )}
              </Typography>
            ) : (
              <TextField
                select
                fullWidth
                required
                label={t('products.agents.fields.loginTenant', 'Login tenant')}
                value={loginTenantId === '' ? '' : String(loginTenantId)}
                onChange={(event) => {
                  setLoginTenantId(Number(event.target.value));
                  setError(null);
                }}
                error={tenantErrored}
                helperText={
                  tenantErrored
                    ? fieldErrorMessage
                    : t(
                        'products.agents.fields.loginTenantHelper',
                        'ACTIVE tenants of this product only.',
                      )
                }
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name} ({tenant.slug})
                  </MenuItem>
                ))}
              </TextField>
            )}
            <RedirectUriEditor
              values={redirectUris}
              onChange={(next) => {
                setRedirectUris(next);
                setError(null);
                setUriHint(null);
              }}
              error={redirectErrored}
              helperText={
                redirectErrored
                  ? fieldErrorMessage
                  : (uriHint ??
                    t(
                      'products.agents.fields.redirectUrisHelper',
                      '1–10 URIs. https://… (no fragment), http://localhost or http://127.0.0.1, or a custom scheme.',
                    ))
              }
              onInvalid={(message) => setUriHint(message)}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={catalogScopes}
              value={scopes}
              onChange={(_event, value) => {
                setScopes(value);
                setError(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('products.agents.fields.scopes', 'Scopes')}
                  error={scopeErrored}
                  helperText={
                    scopeErrored
                      ? fieldErrorMessage
                      : t(
                          'products.agents.fields.scopesHelper',
                          "A subset of this product's published scopes ({{slug}}:*). Empty is allowed.",
                          { slug: productSlug },
                        )
                  }
                />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          form="agent-client-form"
          type="submit"
          variant="contained"
          disabled={pending || !canSubmit}
        >
          {t('products.agents.create', 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface AgentClientDrawerProps {
  client: AgentClientResponse | null;
  productId: number;
  catalogScopes: string[];
  busy: boolean;
  onClose: () => void;
  onForbidden: () => void;
  onUpdated: (updated: AgentClientResponse) => void;
  onToggleEnabled: (enabled: boolean) => void;
}

function AgentClientDrawer({
  client,
  productId,
  catalogScopes,
  busy,
  onClose,
  onForbidden,
  onUpdated,
  onToggleEnabled,
}: AgentClientDrawerProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [uriHint, setUriHint] = useState<string | null>(null);
  const [savingRedirects, setSavingRedirects] = useState(false);
  const [savingScopes, setSavingScopes] = useState(false);

  const clientDbId = client?.clientDbId;
  const [boundId, setBoundId] = useState<number | null>(null);
  if ((clientDbId ?? null) !== boundId) {
    setBoundId(clientDbId ?? null);
    setRedirectUris(client?.redirectUris ? [...client.redirectUris] : []);
    setScopes(client?.scopes ? [...client.scopes] : []);
    setRedirectError(null);
    setScopeError(null);
    setUriHint(null);
  }

  async function handleSaveRedirects() {
    if (!client) return;
    setRedirectError(null);
    setSavingRedirects(true);
    try {
      const updated = await apiAdmin.agentClients.setRedirectUris(
        productId,
        client.clientDbId,
        redirectUris,
      );
      onUpdated(updated);
      snack.success(t('products.agents.redirectsSaved', 'Redirect URIs saved.'));
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError && err.code === 'INVALID_REDIRECT_URI') {
        setRedirectError(t('products.agents.errors.INVALID_REDIRECT_URI', err.code));
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      }
    } finally {
      setSavingRedirects(false);
    }
  }

  async function handleSaveScopes() {
    if (!client) return;
    setScopeError(null);
    setSavingScopes(true);
    try {
      const updated = await apiAdmin.agentClients.setScopes(productId, client.clientDbId, scopes);
      onUpdated(updated);
      snack.success(t('products.agents.scopesSaved', 'Scopes saved.'));
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError && err.code === 'INVALID_SCOPE') {
        setScopeError(t('products.agents.errors.INVALID_SCOPE', err.code));
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      }
    } finally {
      setSavingScopes(false);
    }
  }

  return (
    <Drawer
      anchor="right"
      open={Boolean(client)}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: 1, sm: 480 }, p: 3 } },
      }}
    >
      {client ? (
        <Stack spacing={2}>
          <Typography variant="h3">{client.clientName}</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {client.clientId}
          </Typography>
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              {t('products.agents.fields.authorizeUrl', 'Authorize URL')}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {client.authorizeUrl}
              </Typography>
              <CopyToClipboardButton
                value={client.authorizeUrl}
                ariaLabel={t('products.agents.copyAuthorizeUrl', 'Copy authorize URL')}
              />
            </Stack>
          </Box>
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              {t('products.agents.fields.tokenUrl', 'Token URL')}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {client.tokenUrl}
              </Typography>
              <CopyToClipboardButton
                value={client.tokenUrl}
                ariaLabel={t('products.agents.copyTokenUrl', 'Copy token URL')}
              />
            </Stack>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              'products.agents.urlHelper',
              "Paste these into the agent platform's OAuth form; PKCE S256, no client secret.",
            )}
          </Typography>
          <RedirectUriEditor
            values={redirectUris}
            onChange={(next) => {
              setRedirectUris(next);
              setRedirectError(null);
              setUriHint(null);
            }}
            error={Boolean(redirectError)}
            helperText={
              redirectError ??
              uriHint ??
              t(
                'products.agents.fields.redirectUrisHelper',
                '1–10 URIs. https://… (no fragment), http://localhost or http://127.0.0.1, or a custom scheme.',
              )
            }
            onInvalid={(message) => setUriHint(message)}
          />
          <Button
            variant="outlined"
            onClick={() => void handleSaveRedirects()}
            disabled={savingRedirects}
          >
            {t('products.agents.saveRedirects', 'Save redirect URIs')}
          </Button>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={catalogScopes}
            value={scopes}
            onChange={(_event, value) => {
              setScopes(value);
              setScopeError(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('products.agents.fields.scopes', 'Scopes')}
                error={Boolean(scopeError)}
                helperText={scopeError ?? undefined}
              />
            )}
          />
          <Button variant="outlined" onClick={() => void handleSaveScopes()} disabled={savingScopes}>
            {t('products.agents.saveScopes', 'Save scopes')}
          </Button>
          <Button
            color={client.enabled ? 'error' : 'primary'}
            variant="outlined"
            disabled={busy}
            onClick={() => onToggleEnabled(!client.enabled)}
          >
            {client.enabled
              ? t('products.agents.disable', 'Disable')
              : t('products.agents.enable', 'Enable')}
          </Button>
        </Stack>
      ) : null}
    </Drawer>
  );
}

interface RedirectUriEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  error?: boolean;
  helperText?: string | null;
  onInvalid?: (message: string) => void;
}

function RedirectUriEditor({
  values,
  onChange,
  error,
  helperText,
  onInvalid,
}: RedirectUriEditorProps) {
  const { t } = useCopy();
  const [input, setInput] = useState('');

  function addUri() {
    const next = input.trim();
    if (!next) return;
    if (values.includes(next)) {
      setInput('');
      return;
    }
    if (values.length >= MAX_REDIRECT_URIS) return;
    if (!isAllowedRedirectUri(next)) {
      onInvalid?.(
        t(
          'products.agents.errors.INVALID_REDIRECT_URI',
          'Redirect URIs must be https://… (no fragment), http://localhost or http://127.0.0.1, or a custom scheme.',
        ),
      );
      return;
    }
    onChange([...values, next]);
    setInput('');
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {t('products.agents.fields.redirectUris', 'Redirect URIs')}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {values.map((uri) => (
          <Chip
            key={uri}
            label={uri}
            onDelete={() => onChange(values.filter((item) => item !== uri))}
            size="small"
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          value={input}
          error={error}
          label={t('products.agents.fields.redirectUriInput', 'Add redirect URI')}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addUri();
            }
          }}
        />
        <Button onClick={addUri} disabled={values.length >= MAX_REDIRECT_URIS}>
          {t('products.agents.addUri', 'Add')}
        </Button>
      </Stack>
      {helperText ? (
        <Typography
          variant="caption"
          sx={{ color: error ? 'error.main' : 'text.secondary', display: 'block', mt: 0.5 }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}
